// src/components/publishing/paste-content-composer.tsx
import React, { useMemo, useState } from "react";
import {
  FileCode2, Globe, Linkedin, ClipboardPaste, Loader2, AlertCircle,
  ChevronDown, ChevronUp,
} from "lucide-react";
import {
  Destination, DESTINATIONS, destinationLabel, Integration,
  PerDestinationContent, HashnodeContent, WordPressContent, LinkedInContent,
} from "@/app/services/publishing/publishing-api";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/alert";
import { HashnodeEditor, WordPressEditor, LinkedInEditor } from "./destination-field-editors";

interface PasteContentComposerProps {
  integrations: Integration[];
  brandName?: string;
  onCreate: (params: {
    destinations: Destination[];
    per_destination_content: PerDestinationContent;
    title_hint?: string;
  }) => Promise<void>;
  creating: boolean;
}

/**
 * Alternative to DraftComposer. Lets the user provide their own content per
 * destination and save it as a draft — skipping Claude entirely. The draft
 * then flows into the same preview → publish flow as AI-drafted ones.
 */
export function PasteContentComposer({
  integrations, brandName, onCreate, creating,
}: PasteContentComposerProps) {
  const connectedSet = useMemo(
    () => new Set(integrations.filter(i => i.status === "active").map(i => i.destination)),
    [integrations],
  );

  const [selected, setSelected] = useState<Set<Destination>>(new Set());
  const [content, setContent] = useState<PerDestinationContent>({});
  const [openSection, setOpenSection] = useState<Destination | null>(null);

  // When the user picks a destination for the first time, auto-open its editor.
  const toggleDestination = (d: Destination) => {
    const next = new Set(selected);
    if (next.has(d)) {
      next.delete(d);
      if (openSection === d) setOpenSection(null);
    } else {
      next.add(d);
      setOpenSection(d);
    }
    setSelected(next);
  };

  const updateContent = (d: Destination, patch: any) => {
    setContent(prev => ({
      ...prev,
      [d]: { ...(prev[d] || {}), ...patch },
    }));
  };

  // Each selected destination must have at least one non-empty required field.
  const completeness = useMemo(() => {
    const status: Record<string, "empty" | "partial" | "ready"> = {};
    for (const d of selected) {
      const c = content[d] || {};
      if (d === "hashnode") {
        const hc = c as HashnodeContent;
        if (!hc.title?.trim() && !hc.content_markdown?.trim()) status[d] = "empty";
        else if (!hc.title?.trim() || !hc.content_markdown?.trim()) status[d] = "partial";
        else status[d] = "ready";
      } else if (d === "wordpress") {
        const wc = c as WordPressContent;
        if (!wc.title?.trim() && !wc.content_html?.trim()) status[d] = "empty";
        else if (!wc.title?.trim() || !wc.content_html?.trim()) status[d] = "partial";
        else status[d] = "ready";
      } else if (d === "linkedin") {
        const lc = c as LinkedInContent;
        if (!lc.text?.trim()) status[d] = "empty";
        else if (lc.text.trim().length < 20) status[d] = "partial";
        else status[d] = "ready";
      }
    }
    return status;
  }, [selected, content]);

  const canCreate = selected.size > 0
    && Array.from(selected).every(d => completeness[d] === "ready")
    && !creating;

  const handleCreate = async () => {
    if (!canCreate) return;
    // Derive a title hint from whichever destination has a usable title
    let title_hint = "";
    for (const d of selected) {
      const c = content[d] || {};
      const t = (c as any).title || (c as any).text || "";
      if (typeof t === "string" && t.trim()) {
        title_hint = t.trim().slice(0, 200);
        break;
      }
    }
    // Filter content to only selected destinations
    const filtered: PerDestinationContent = {};
    for (const d of selected) {
      filtered[d] = content[d] as any;
    }
    await onCreate({
      destinations: Array.from(selected),
      per_destination_content: filtered,
      title_hint: title_hint || undefined,
    });
  };

  const anyDisconnected = Array.from(selected).some(d => !connectedSet.has(d));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{
        padding: "10px 12px", borderRadius: 9,
        backgroundColor: "rgba(13,148,136,0.04)",
        border: "1px solid rgba(13,148,136,0.15)",
        display: "flex", alignItems: "flex-start", gap: 10,
      }}>
        <ClipboardPaste style={{
          width: 16, height: 16, color: "#0D9488",
          flexShrink: 0, marginTop: 1,
        }} />
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: 13, fontWeight: 700, color: "var(--foreground)",
            marginBottom: 2,
          }}>
            Paste your own content
          </div>
          <div style={{ fontSize: 12, color: "var(--muted-foreground)", lineHeight: 1.5 }}>
            Pick destinations, then paste or type your content for each. No AI
            generation — your words go through unchanged, but we'll still check
            length and format rules before publishing.
          </div>
        </div>
      </div>

      {/* Step 1 — pick destinations */}
      <div>
        <Label>Destinations</Label>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {DESTINATIONS.map(d => (
            <DestinationCheckbox
              key={d}
              destination={d}
              checked={selected.has(d)}
              connected={connectedSet.has(d)}
              status={completeness[d]}
              onToggle={() => toggleDestination(d)}
            />
          ))}
        </div>
        {anyDisconnected && (
          <div className="mt-2">
            <Alert variant="destructive">
              <AlertCircle />
              <AlertTitle>Some destinations aren't connected</AlertTitle>
              <AlertDescription>
                You can still create the draft, but you won't be able to publish
                to destinations that aren't connected. Connect them from the
                Integrations page first.
              </AlertDescription>
            </Alert>
          </div>
        )}
      </div>

      {/* Step 2 — per-destination editors */}
      {selected.size > 0 && (
        <div>
          <Label>Content per destination</Label>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {Array.from(selected).map(d => (
              <PerDestinationSection
                key={d}
                destination={d}
                content={content[d] || {}}
                isOpen={openSection === d}
                completenessStatus={completeness[d]}
                onToggle={() => setOpenSection(openSection === d ? null : d)}
                onChange={patch => updateContent(d, patch)}
              />
            ))}
          </div>
        </div>
      )}

      <div style={{
        display: "flex", gap: 10, justifyContent: "flex-end",
        flexWrap: "wrap", paddingTop: 4,
      }}>
        <button
          onClick={handleCreate}
          disabled={!canCreate}
          style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            padding: "10px 18px", borderRadius: 9,
            border: "none",
            backgroundColor: canCreate ? "#0D9488" : "var(--border)",
            color: "white",
            cursor: canCreate ? "pointer" : "not-allowed",
            fontSize: 13, fontWeight: 600,
          }}
        >
          {creating
            ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
            : <ClipboardPaste style={{ width: 14, height: 14 }} />
          }
          {creating ? "Creating draft…" : "Create draft from pasted content"}
        </button>
      </div>
    </div>
  );
}

// ─── Destination checkbox ────────────────────────────────────────────────────

function DestinationCheckbox({
  destination, checked, connected, status, onToggle,
}: {
  destination: Destination;
  checked: boolean;
  connected: boolean;
  status?: "empty" | "partial" | "ready";
  onToggle: () => void;
}) {
  const meta = {
    hashnode:  { Icon: FileCode2, color: "#2962FF", blurb: "Title + markdown body" },
    wordpress: { Icon: Globe,     color: "#21759B", blurb: "Title + HTML body" },
    linkedin:  { Icon: Linkedin,  color: "#0A66C2", blurb: "Plain text post" },
  }[destination];

  return (
    <button
      onClick={onToggle}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "9px 11px", borderRadius: 9,
        border: `1px solid ${checked ? "#0D9488" : "var(--border)"}`,
        backgroundColor: checked ? "rgba(13,148,136,0.06)" : "var(--muted)",
        cursor: "pointer", textAlign: "left",
      }}
    >
      <div
        style={{
          width: 16, height: 16, borderRadius: 4,
          border: `2px solid ${checked ? "#0D9488" : "var(--border)"}`,
          backgroundColor: checked ? "#0D9488" : "transparent",
          flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        {checked && (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>

      <div style={{
        width: 28, height: 28, borderRadius: 7,
        backgroundColor: "var(--background)", border: "1px solid var(--border)",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <meta.Icon style={{ width: 14, height: 14, color: meta.color }} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)" }}>
          {destinationLabel(destination)}
        </div>
        <div style={{ fontSize: 11, color: "var(--muted-foreground)" }}>
          {meta.blurb}
          {" · "}
          {connected
            ? <span style={{ color: "#10B981", fontWeight: 600 }}>Connected</span>
            : <span style={{ color: "#D97706", fontWeight: 600 }}>Not connected</span>
          }
          {checked && status && (
            <>
              {" · "}
              {status === "ready" && <span style={{ color: "#10B981", fontWeight: 600 }}>Ready</span>}
              {status === "partial" && <span style={{ color: "#D97706", fontWeight: 600 }}>Fill all fields</span>}
              {status === "empty" && <span style={{ color: "var(--muted-foreground)", fontWeight: 600 }}>Empty</span>}
            </>
          )}
        </div>
      </div>
    </button>
  );
}

// ─── Per-destination accordion section ───────────────────────────────────────

function PerDestinationSection({
  destination, content, isOpen, completenessStatus, onToggle, onChange,
}: {
  destination: Destination;
  content: any;
  isOpen: boolean;
  completenessStatus?: "empty" | "partial" | "ready";
  onToggle: () => void;
  onChange: (patch: any) => void;
}) {
  const meta = {
    hashnode:  { Icon: FileCode2, color: "#2962FF" },
    wordpress: { Icon: Globe,     color: "#21759B" },
    linkedin:  { Icon: Linkedin,  color: "#0A66C2" },
  }[destination];

  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: 10,
        backgroundColor: "var(--muted)",
        overflow: "hidden",
      }}
    >
      <button
        onClick={onToggle}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 10,
          padding: "11px 13px",
          border: "none", backgroundColor: "transparent", cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div style={{
          width: 24, height: 24, borderRadius: 6,
          backgroundColor: "var(--background)", border: "1px solid var(--border)",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <meta.Icon style={{ width: 13, height: 13, color: meta.color }} />
        </div>
        <span style={{
          flex: 1, fontSize: 13, fontWeight: 600, color: "var(--foreground)",
        }}>
          {destinationLabel(destination)}
        </span>

        {completenessStatus === "ready" && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 999,
            backgroundColor: "rgba(16,185,129,0.1)",
            color: "#10B981",
            border: "1px solid rgba(16,185,129,0.25)",
            textTransform: "uppercase", letterSpacing: "0.04em",
          }}>
            Ready
          </span>
        )}
        {completenessStatus === "partial" && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 999,
            backgroundColor: "rgba(245,158,11,0.08)",
            color: "#D97706",
            border: "1px solid rgba(245,158,11,0.25)",
            textTransform: "uppercase", letterSpacing: "0.04em",
          }}>
            Incomplete
          </span>
        )}

        {isOpen
          ? <ChevronUp   style={{ width: 15, height: 15, color: "var(--muted-foreground)" }} />
          : <ChevronDown style={{ width: 15, height: 15, color: "var(--muted-foreground)" }} />
        }
      </button>
      {isOpen && (
        <div style={{
          padding: "6px 14px 14px 14px",
          backgroundColor: "var(--background)",
          borderTop: "1px solid var(--border)",
        }}>
          {destination === "hashnode" && (
            <HashnodeEditor content={content as HashnodeContent} onChange={onChange} />
          )}
          {destination === "wordpress" && (
            <WordPressEditor content={content as WordPressContent} onChange={onChange} />
          )}
          {destination === "linkedin" && (
            <LinkedInEditor content={content as LinkedInContent} onChange={onChange} />
          )}
        </div>
      )}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <p style={{
      fontSize: 11, fontWeight: 700, color: "var(--muted-foreground)",
      textTransform: "uppercase", letterSpacing: "0.05em",
      marginBottom: 6, margin: "0 0 6px 0",
    }}>
      {children}
    </p>
  );
}